import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { Spinner, Toaster } from './components/ui';

/* ── Route-level code splitting ─────────────────────────────────────────────
   Each page is loaded only when the route is first visited.
   To add a new page: lazy-import it here and add a <Route> below.
──────────────────────────────────────────────────────────────────────────── */
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AssetsPage = lazy(() => import('./pages/AssetsPage'));
const NewSpacePage = lazy(() => import('./pages/NewSpacePage'));
const SpaceEditorPage = lazy(() => import('./pages/SpaceEditorPage'));
const SpaceViewerPage = lazy(() => import('./pages/SpaceViewerPage'));
const PlaygroundPage = lazy(() => import('./pages/PlaygroundPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

function PageLoader(): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

export default function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Toaster />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/playground" element={<PlaygroundPage />} />
              <Route path="/u/:username/:slug" element={<SpaceViewerPage />} />

              {/* Protected */}
              <Route
                path="/me"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/u/:username"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/u/:username/assets"
                element={
                  <ProtectedRoute>
                    <AssetsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/u/:username/s/new"
                element={
                  <ProtectedRoute>
                    <NewSpacePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/u/:username/s/:id/edit"
                element={
                  <ProtectedRoute>
                    <SpaceEditorPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                }
              />

              {/* Fallback */}
              <Route path="/" element={<Navigate to="/me" replace />} />
              <Route path="*" element={<Navigate to="/me" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
